import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  BarChart3,
  Clock,
  Users,
  Search,
  Filter,
  Plus,
  TrendingUp,
  Target,
  CheckCircle2,
  ClipboardList,
  Loader2,
  AlertCircle
} from 'lucide-react';
import {
  surveyService,
  surveyStatsService,
  pollService,
  Survey,
  SurveyCategory,
  Poll
} from '../../services/supabaseSurveys';

interface SurveysHubProps {
  memberId: string;
}

// Composant CompactSurveyCard
const CompactSurveyCard: React.FC<{
  survey: Survey & { hasResponded?: boolean };
  onViewDetails: (survey: Survey) => void;
}> = ({ survey, onViewDetails }) => {
  const getStatusColor = (status: string, hasResponded: boolean) => {
    if (hasResponded) return 'bg-primary-green/10 text-primary-green border border-primary-green/20';
    if (status === 'closed') return 'bg-gray-100 text-gray-700 border border-gray-200';
    return 'bg-primary-blue/10 text-primary-blue border border-primary-blue/20';
  };

  const getStatusIcon = (status: string, hasResponded: boolean) => {
    if (hasResponded) return <CheckCircle2 size={14} />;
    if (status === 'closed') return <ClipboardList size={14} />;
    return <Clock size={14} />;
  };

  const getStatusText = (status: string, hasResponded: boolean) => {
    if (hasResponded) return 'Repondu';
    if (status === 'closed') return 'Terminee';
    return 'En cours';
  };

  const progress = survey.target_responses
    ? Math.round((survey.current_responses / survey.target_responses) * 100)
    : 0;

  const category = survey.category as SurveyCategory | undefined;

  // Calculer le temps estime
  const estimatedTime = survey.short_description?.match(/(\d+).*min/)?.[1] || '5-10';

  return (
    <div
      className="group card-udb p-6 hover:shadow-udb-lg hover:scale-105 transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-primary-blue/30"
      onClick={() => onViewDetails(survey)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 text-base line-clamp-2 leading-tight mb-3 group-hover:text-primary-blue transition-colors">
            {survey.title}
          </h3>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium ${getStatusColor(survey.status, survey.hasResponded || false)}`}>
              {getStatusIcon(survey.status, survey.hasResponded || false)}
              {getStatusText(survey.status, survey.hasResponded || false)}
            </span>
            {survey.featured && (
              <span className="bg-primary-yellow/20 text-primary-blue text-xs px-2 py-1 font-medium flex items-center gap-1">
                <span>*</span>
                Vedette
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="space-y-3 mb-4">
        {category && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span
              className="w-3 h-3 flex-shrink-0"
              style={{ backgroundColor: category.color }}
            ></span>
            <span className="truncate font-medium">{category.name}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users size={14} className="text-primary-blue flex-shrink-0" />
          <span className="truncate">
            {survey.current_responses.toLocaleString()}
            {survey.target_responses ? ` / ${survey.target_responses.toLocaleString()}` : ''} reponses
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock size={14} className="text-primary-blue flex-shrink-0" />
          <span className="truncate">{estimatedTime} minutes</span>
        </div>
      </div>

      {/* Progress bars */}
      {survey.target_responses && (
        <div className="space-y-2 mb-3">
          <div>
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span className="font-medium">Participation</span>
              <span className="font-semibold text-primary-blue">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 h-2 overflow-hidden">
              <div
                className={`h-2 transition-all duration-500 ${
                  survey.hasResponded ? 'bg-primary-green' : 'bg-primary-blue'
                }`}
                style={{ width: `${Math.min(progress, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant PollCard
const PollCard: React.FC<{
  poll: Poll & { hasVoted?: boolean };
  onVote: (pollId: number, optionId: number) => void;
}> = ({ poll, onVote }) => {
  const totalVotes = poll.options?.reduce((sum, opt) => sum + opt.vote_count, 0) || 0;

  return (
    <div className="card-udb p-6">
      <h3 className="font-medium text-gray-900 mb-4">{poll.question}</h3>
      <div className="space-y-3">
        {poll.options?.map(option => {
          const percentage = totalVotes > 0 ? Math.round((option.vote_count / totalVotes) * 100) : 0;
          return (
            <button
              key={option.id}
              onClick={() => !poll.hasVoted && onVote(poll.id, option.id)}
              disabled={poll.hasVoted}
              className={`w-full text-left p-3 border-2 rounded transition-all ${
                poll.hasVoted
                  ? 'border-gray-200 cursor-default'
                  : 'border-gray-100 hover:border-primary-blue cursor-pointer'
              }`}
            >
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">{option.option_text}</span>
                {(poll.hasVoted || poll.results_visible) && (
                  <span className="text-sm text-primary-blue font-semibold">{percentage}%</span>
                )}
              </div>
              {(poll.hasVoted || poll.results_visible) && (
                <div className="w-full bg-gray-200 h-1.5 rounded overflow-hidden">
                  <div
                    className="h-1.5 bg-primary-blue transition-all duration-500"
                    style={{ width: `${percentage}%`, backgroundColor: option.color || '#00469b' }}
                  ></div>
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-4 text-xs text-gray-500 text-center">
        {totalVotes} votes
      </div>
    </div>
  );
};

const SurveysHub: React.FC<SurveysHubProps> = ({ memberId }) => {
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [surveys, setSurveys] = useState<(Survey & { hasResponded?: boolean })[]>([]);
  const [completedSurveys, setCompletedSurveys] = useState<Survey[]>([]);
  const [memberResponses, setMemberResponses] = useState<Survey[]>([]);
  const [polls, setPolls] = useState<(Poll & { hasVoted?: boolean })[]>([]);
  const [stats, setStats] = useState({ totalResponses: 0, surveysCompleted: 0, pollsVoted: 0, opinionPoints: 0 });
  const [globalStats, setGlobalStats] = useState({ totalActiveSurveys: 0, totalActivePolls: 0, totalParticipants: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les donnees
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Charger en parallele
      const [
        activeSurveys,
        closedSurveys,
        memberSurveys,
        activePolls,
        memberStats,
        globalStatsData
      ] = await Promise.all([
        surveyService.getActive(),
        surveyService.getCompleted(),
        memberId ? surveyService.getMemberResponses(memberId) : Promise.resolve([]),
        pollService.getActive(),
        memberId ? surveyStatsService.getMemberStats(memberId) : Promise.resolve({ totalResponses: 0, surveysCompleted: 0, pollsVoted: 0, opinionPoints: 0 }),
        surveyStatsService.getGlobalStats()
      ]);

      // Marquer les enquetes auxquelles le membre a repondu
      const respondedIds = new Set(memberSurveys.map(s => s.id));
      const surveysWithStatus = activeSurveys.map(s => ({
        ...s,
        hasResponded: respondedIds.has(s.id)
      }));

      // Verifier les votes pour les sondages
      const pollsWithStatus = await Promise.all(
        activePolls.map(async (poll) => {
          const hasVoted = memberId ? await pollService.hasVoted(poll.id, memberId) : false;
          return { ...poll, hasVoted };
        })
      );

      setSurveys(surveysWithStatus);
      setCompletedSurveys(closedSurveys);
      setMemberResponses(memberSurveys);
      setPolls(pollsWithStatus);
      setStats(memberStats);
      setGlobalStats(globalStatsData);
    } catch (err) {
      console.error('Erreur chargement enquetes:', err);
      setError('Impossible de charger les enquetes. Veuillez reessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSurveyClick = (survey: Survey) => {
    // TODO: Ouvrir la modal de l'enquete ou naviguer vers la page
    console.log('Ouvrir enquete:', survey);
  };

  const handlePollVote = async (pollId: number, optionId: number) => {
    try {
      await pollService.vote(pollId, optionId, memberId);
      // Recharger les donnees
      loadData();
    } catch (err) {
      console.error('Erreur vote:', err);
    }
  };

  // Filtrer les enquetes
  const filteredSurveys = surveys.filter(survey => {
    const matchesTab =
      activeTab === 'all' ||
      (activeTab === 'active' && survey.status === 'published' && !survey.hasResponded) ||
      (activeTab === 'my_responses' && survey.hasResponded) ||
      (activeTab === 'completed' && survey.status === 'closed');

    const matchesSearch =
      !searchQuery ||
      survey.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      survey.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (survey.category as SurveyCategory)?.name.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesTab && matchesSearch;
  });

  // Pour l'onglet completed, utiliser les enquetes fermees
  // Type consistent pour tous les cas avec hasResponded optionnel
  const displaySurveys: (Survey & { hasResponded?: boolean })[] = activeTab === 'completed'
    ? completedSurveys.filter(s =>
        !searchQuery ||
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.description.toLowerCase().includes(searchQuery.toLowerCase())
      ).map(s => ({ ...s, hasResponded: false }))
    : activeTab === 'my_responses'
      ? memberResponses.filter(s =>
          !searchQuery ||
          s.title.toLowerCase().includes(searchQuery.toLowerCase())
        ).map(s => ({ ...s, hasResponded: true }))
      : filteredSurveys;

  const statsDisplay = [
    { label: 'Enquetes actives', value: globalStats.totalActiveSurveys.toString(), icon: MessageSquare },
    { label: 'Mes reponses', value: stats.surveysCompleted.toString(), icon: CheckCircle2 },
    { label: 'Sondages votes', value: stats.pollsVoted.toString(), icon: Users },
    { label: 'Points d\'opinion', value: stats.opinionPoints.toString(), icon: Target }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-blue animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement des enquetes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">Erreur</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={loadData} className="btn-udb-primary">
            Reessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header Section */}
      <section className="relative bg-primary-blue text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-float-up"
                style={{
                  width: Math.random() * 3 + 1 + 'px',
                  height: Math.random() * 3 + 1 + 'px',
                  left: Math.random() * 100 + '%',
                  bottom: '-20px',
                  backgroundColor: i % 3 === 0 ? '#ffd400' : i % 3 === 1 ? '#ffffff' : '#00469b',
                  opacity: Math.random() * 0.6 + 0.2,
                  animationDuration: `${Math.random() * 15 + 10}s`,
                  animationDelay: `${Math.random() * 10}s`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-light text-white">
                Ecouter pour
                <span className="text-primary-yellow font-normal ml-2">mieux servir</span>
              </h1>
              <p className="text-white/80 mt-1 font-light">
                Votre opinion faconne l'avenir du Gabon
              </p>
            </div>

            <div className="hidden md:flex items-center gap-6">
              <div className="text-right">
                <div className="text-2xl font-light text-primary-yellow">
                  {globalStats.totalActiveSurveys}
                </div>
                <div className="text-white/60 text-sm">Enquetes actives</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-light text-primary-yellow">
                  {globalStats.totalParticipants.toLocaleString()}
                </div>
                <div className="text-white/60 text-sm">Participants</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation par onglets */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'active'
                  ? 'border-primary-blue text-primary-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <MessageSquare size={18} />
              Enquetes actives
            </button>
            <button
              onClick={() => setActiveTab('my_responses')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'my_responses'
                  ? 'border-primary-yellow text-primary-yellow'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users size={18} />
              Mes reponses
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'completed'
                  ? 'border-primary-green text-primary-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CheckCircle2 size={18} />
              Terminees
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'all'
                  ? 'border-primary-yellow text-primary-yellow'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 size={18} />
              Toutes
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 -mt-4">
          {statsDisplay.map((stat, index) => (
            <div key={index} className="card-udb p-6 text-center hover:shadow-udb-lg hover:scale-105 transition-all duration-300">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-primary-blue/10 rounded-full flex items-center justify-center">
                  <stat.icon className="text-primary-blue" size={24} />
                </div>
              </div>
              <div className="text-2xl font-light text-primary-blue mb-1">{stat.value}</div>
              <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Sondages rapides */}
        {activeTab === 'active' && polls.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Sondages rapides</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {polls.slice(0, 3).map(poll => (
                <PollCard key={poll.id} poll={poll} onVote={handlePollVote} />
              ))}
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="card-udb p-8 mb-8">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher une enquete..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-100 focus:border-primary-blue outline-none transition-all bg-white/70"
              />
            </div>
            <div className="flex items-center gap-2">
              <button className="flex items-center gap-2 px-6 py-3 border-2 border-gray-100 hover:border-primary-green hover:text-primary-green transition-all bg-white/70">
                <Filter size={20} />
                Filtres
              </button>
              {activeTab === 'active' && (
                <button className="btn-udb-primary flex items-center gap-2">
                  <Plus size={20} />
                  Proposer une enquete
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Enquetes vedettes */}
        {activeTab === 'active' && displaySurveys.filter(s => s.featured && !s.hasResponded).length > 0 && (
          <div className="mb-12">
            <div className="flex items-center space-x-2 mb-8">
              <TrendingUp className="w-6 h-6 text-primary-yellow" />
              <span className="text-primary-blue text-sm uppercase tracking-wider font-semibold">Enquetes vedettes</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8 leading-tight">
              Votre opinion <span className="text-primary-blue font-normal">compte</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {displaySurveys.filter(s => s.featured && !s.hasResponded).map(survey => (
                <CompactSurveyCard
                  key={survey.id}
                  survey={survey}
                  onViewDetails={handleSurveyClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Toutes les enquetes */}
        <div>
          <h2 className="text-2xl font-light text-gray-900 mb-8">
            {activeTab === 'all' ? 'Toutes les enquetes' :
             activeTab === 'active' ? 'Enquetes actives' :
             activeTab === 'my_responses' ? 'Mes reponses' : 'Enquetes terminees'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displaySurveys.map(survey => (
              <CompactSurveyCard
                key={survey.id}
                survey={survey as Survey & { hasResponded?: boolean }}
                onViewDetails={handleSurveyClick}
              />
            ))}
          </div>

          {displaySurveys.length === 0 && (
            <div className="text-center py-12">
              <MessageSquare className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune enquete trouvee</h3>
              <p className="text-gray-600">
                {searchQuery
                  ? `Aucune enquete ne correspond a "${searchQuery}"`
                  : "Aucune enquete disponible dans cette categorie"
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveysHub;
